import CachedIcon from "mdi-react/CachedIcon";
import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Button,
  InputGroup,
  Dropdown,
  Modal,
  Row,
  Col,
  Spinner,
  Badge,
} from "react-bootstrap";
import TableComponent from "../TableComponent";
import {
  DeleteIcon,
  FilterCollapseIcon,
  FilterTwoIcon,
  LoaderIcon,
  ViewIcon,
  AddCircleIcon,
  EditIcon,
} from "../Icons";
import { useLocalStorage, useQueryParams } from "../../utils/hooks";
import useDebounce from "../../utils/hooks";
import { useQuery, useMutation } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import queryString from "query-string";
import {
  formatDate,
  paginationOptions,
  fetchActionsUtil,
  getCurrencySymbol,
  courseCategoryOption,
} from "../../utils/helpers";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import ReactPaginate from "react-paginate";
import NoTableItem from "../utils/NoTableItem";
import { isEmpty, truncate } from "lodash";
import { useNavigate, useOutletContext } from "react-router-dom";
import ConfirmDialog from "../ConfirmDialogue";
import Select from "react-select";
import { toast } from "react-toastify";
import CourseCreationModal from "./CourseCreationModal";
import CertificationTypeModal from "./CertificationTypeModal";
import currency from "currency.js";

function Courses() {
  const navMetaData = useOutletContext();
  const { backendUrl, token, user } = useAuth();
  const [showFilter, setShowFilter] = useLocalStorage(
    "showCoursesFilter",
    true,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  // const [certificationTypes, setCertificationTypes] = useState([]);
  const [showCertificationTypeModal, setShowCertificationTypeModal] = useState(
    false,
  );

  const [showCourseModal, setShowCourseModal] = useState(false);

  const initialFilterParams = {
    courseCategory: "",
    searchQuery: "",
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  const fetchCourses = async (query) => {
    try {
      const data = await fetchActionsUtil(
        `${backendUrl}/api/partnership/courses?${queryString.stringify(query)}`,
        "GET",
        token,
      );
      return data;
    } catch (error) {
      toast.error("Error getting courses, please refresh the page");
      throw error;
    }
  };

  const deleteCourseMutation = useMutation(
    (id) =>
      fetchActionsUtil(
        `${backendUrl}/api/partnership/delete-course/${id}`,
        "DELETE",
        token,
      ),
    {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: ({ data }) => {
        refetch();
        toast.success(data?.message || "Course deleted successfully");
        setIsLoading(false);
      },
      onError: (error) => {
        toast.error(error?.message || "Unable to delete course");
        setIsLoading(false);
      },
    },
  );

  const deleteCourse = async (id) => {
    if (
      await ConfirmDialog({
        title: "Are you sure you want to delete this course?",
        description: "You cannot retrieve a deleted course.",
        type: "danger",
      })
    ) {
      deleteCourseMutation.mutate(id);
    }
  };

  // Sync debounced filter params with query params
  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
  }, [debouncedFilterParams]);

  const { data, refetch, isFetching, isSuccess } = useQuery(
    ["GET_COURSES", queryParams],
    () => fetchCourses(queryParams),
    {
      enabled: true,
      keepPreviousData: true,
      onSuccess: (responseData) => {
        // Update courses list
        setCourses(responseData?.data);

        // Update metadata/stats
        if (responseData) {
          navMetaData({
            stat1: {
              value: responseData?.totalCourseCount || 0,
              label: "Total Courses",
            },
            stat2: {
              value: responseData?.activeCourseCount || 0,
              label: "Active Courses",
            },
            stat3: {
              value: responseData?.inactiveCourseCount || 0,
              label: "Inactive Courses",
            },
          });
        }
      },
    },
  );

  // // Fetch certification types using useQuery
  // useQuery(
  //   ["GET_CERTIFICATION_TYPES"],
  //   async () => {
  //     const data = await fetchActionsUtil(
  //       `${backendUrl}/api/partnership/certification-types`,
  //       "GET",
  //       token,
  //     );
  //     return (data.data || data || []).map((type) => ({
  //       value: type.id,
  //       label: type.name,
  //     }));
  //   },
  //   {
  //     enabled: true,
  //     keepPreviousData: true,
  //     onSuccess: (types) => {
  //       setCertificationTypes(types);
  //     },
  //     onError: (error) => {
  //       console.log("Error fetching certification types:", error);
  //     },
  //   },
  // );

  const handleFilterParamsChange = (e) => {
    const { name, value } = e.target;
    setFilterParams({
      ...filterParams,
      [name]: value,
    });
  };

  const search = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const reset = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSearchQueryChange = (e) => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const tableHead = () => {
    return (
      <thead className="position-sticky top-0">
        <tr>
          <th>Title</th>
          <th>Certification Category</th>
          <th>Price</th>
          <th>Duration (Hours)</th>
          <th>Difficulty Level</th>
          <th>Status</th>
          <th>Created At</th>
          <th>Actions</th>
        </tr>
      </thead>
    );
  };

  const tableData = (el, index) => {
    return (
      <>
        <td>{truncate(el?.title, { length: 30 })}</td>
        <td>{el?.certification_category || "-"}</td>
        <td>
          {currency(el?.price).format({
            symbol: getCurrencySymbol(el?.currency),
            separator: ",",
          })}
        </td>
        <td>{el?.duration_hours || "-"}</td>
        <td>{el?.difficulty_level || "-"}</td>
        <td>
          <Badge bg={Number(el?.is_active) === 1 ? "success" : "danger"}>
            {Number(el?.is_active) === 1 ? "Active" : "Inactive"}
          </Badge>
        </td>
        <td>{formatDate(el?.created_at)}</td>
        <td>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="bg-white border-0"
              bsPrefix="print more"
            >
              <DotsVerticalIcon size={20} />
            </Dropdown.Toggle>

            <Dropdown.Menu
              popperConfig={{
                strategy: "fixed",
              }}
              renderOnMount
              className="dropdown-with-icons"
            >
              <Dropdown.Item
                as={"button"}
                onClick={() => {
                  setSelectedCourse(el);
                  // TODO: Implement view modal
                }}
              >
                <ViewIcon className="me-2" size={16} />
                View
              </Dropdown.Item>
              <Dropdown.Item
                as={"button"}
                onClick={() => {
                  setSelectedCourse(el);
                  setShowCourseModal(true);
                }}
              >
                <EditIcon className="me-2" size={16} />
                Edit
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                as={"button"}
                className="text-danger"
                onClick={() => deleteCourse(el.id)}
              >
                <DeleteIcon className="me-2" size={16} />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </>
    );
  };

  return (
    <>
      <main className="cash-book">
        <div className="content">
          <div className="content-main pt-3">
            <div className={`filter-area ${showFilter ? "open" : "close"}`}>
              <div className="filter-header">
                <FilterTwoIcon /> Filters
                <Button
                  variant="white"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <FilterCollapseIcon />
                </Button>
              </div>
              <div className="filter-body">
                <Form>
                  <div className="body">
                    <Form.Group className="mb-3 pb-2 mb-2-5">
                      <Form.Label>Search (Title or Description)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          name="searchQuery"
                          value={filterParams.searchQuery}
                          placeholder="Search by title or description"
                          onChange={handleFilterParamsChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3 pb-2 mb-2-5">
                      <Form.Label>Certification Cetgory</Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        placeholder="Certification Category"
                        name="courseCategory"
                        value={courseCategoryOption.find(
                          (cert) => cert.value === filterParams?.courseCategory,
                        )}
                        onChange={(e) => {
                          setFilterParams({
                            ...filterParams,
                            courseCategory: e?.value || "",
                          });
                        }}
                        options={courseCategoryOption}
                        isClearable
                      />
                    </Form.Group>

                    <hr className="mt-3 mb-4" />
                  </div>

                  <div className="buttons rounded">
                    <Button onClick={reset} variant="white">
                      Reset
                    </Button>
                    <Button onClick={search} variant="primary">
                      Search
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
            <div className="content-body">
              <header>
                <h1>
                  {!showFilter && (
                    <button
                      onClick={() => setShowFilter(!showFilter)}
                      className="btn filter"
                    >
                      <FilterTwoIcon />
                    </button>
                  )}
                  Courses Management
                  <button
                    title="Refresh"
                    className="btn text-primary"
                    onClick={() => {
                      refetch();
                    }}
                  >
                    <CachedIcon />
                  </button>
                </h1>
                <div className="actions">
                  {/* <Button
                    variant="primary"
                    onClick={() => setShowCertificationTypeModal(true)}
                  >
                    <AddCircleIcon className="me-2" />+ Create Certification
                    Type
                  </Button> */}
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSelectedCourse(null);
                      setShowCourseModal(true);
                    }}
                  >
                    <AddCircleIcon className="me-2" />+ Create New Course
                  </Button>
                </div>
              </header>

              <div className="m-3">
                <TableComponent
                  responsive
                  borderless
                  striped
                  tableHeadsFunction={tableHead}
                  mainDataArray={courses || []}
                  className="product-table text-nowrap"
                  tableDataRowFunction={tableData}
                />

                {!isFetching && isSuccess && isEmpty(courses) ? (
                  <NoTableItem queryParams={queryParams} />
                ) : null}
              </div>

              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={(e) => handleSearchQueryChange(e)}
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>

                <ReactPaginate
                  {...paginationOptions}
                  pageCount={
                    Math.ceil(
                      (data?.pagination?.total ||
                        data?.total ||
                        courses?.length ||
                        0) / queryParams.limit,
                    ) || 1
                  }
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={0}
                  onPageChange={({ selected }) => {
                    document.body.scrollTop = document.documentElement.scrollTop = 0;
                    setQueryParams({
                      ...queryParams,
                      page: selected + 1,
                    });
                  }}
                  forcePage={queryParams.page - 1}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      {showCourseModal && (
        <CourseCreationModal
          show={showCourseModal}
          onHide={() => setShowCourseModal(false)}
          onSuccess={() => {
            refetch();
            setShowCourseModal(false);
          }}
          mode={selectedCourse ? "edit" : "create"}
          course={selectedCourse}
          refetch={refetch}
          // certificationTypes={certificationTypes}
        />
      )}

      {showCertificationTypeModal && (
        <CertificationTypeModal
          show={showCertificationTypeModal}
          onHide={setShowCertificationTypeModal}
          onSuccess={() => {
            refetch();
            setShowCertificationTypeModal(false);
          }}
        />
      )}

      {isLoading && (
        <div className="global-spinner">
          <LoaderIcon className="spin text-primary" />
        </div>
      )}
    </>
  );
}

export default Courses;
